import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Campaign as CampaignIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  Instagram as InstagramIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

interface Campaign {
  id: string;
  name: string;
  type: string;
  status: string;
  totalRecipients?: number;
  sentCount?: number;
  deliveredCount?: number;
  openRate?: number;
  clickRate?: number;
  createdAt: string;
  scheduledAt?: string | null;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'running':
      return 'info';
    case 'scheduled':
      return 'warning';
    case 'draft':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'completed':
      return 'Concluída';
    case 'running':
      return 'Em execução';
    case 'scheduled':
      return 'Agendada';
    case 'draft':
      return 'Rascunho';
    default:
      return 'Desconhecido';
  }
};

const Campaigns: React.FC = () => {
  const navigate = useNavigate();
  const apiUrl = getApiUrl();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    type: 'broadcast',
    message: '',
    channels: [] as string[],
    audience: 'all',
    scheduledDate: '',
    scheduledTime: '',
  });

  // Buscar campanhas do backend
  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Buscar campanhas de diferentes tipos
      const [disparosRes, smsRes, followupsRes] = await Promise.allSettled([
        axios.get(`${apiUrl}/disparos`),
        axios.get(`${apiUrl}/sms-campaigns`),
        axios.get(`${apiUrl}/followups/campaigns`),
      ]);

      const allCampaigns: Campaign[] = [];

      // Processar campanhas de disparos
      if (disparosRes.status === 'fulfilled' && disparosRes.value.data.success) {
        const disparos = disparosRes.value.data.data || [];
        disparos.forEach((camp: any) => {
          allCampaigns.push({
            id: camp.id,
            name: camp.name || 'Campanha sem nome',
            type: 'disparo',
            status: camp.status || 'draft',
            totalRecipients: camp.leads?.length || 0,
            sentCount: camp.stats?.sent || 0,
            deliveredCount: camp.stats?.delivered || 0,
            createdAt: camp.created_at || new Date().toISOString(),
            scheduledAt: camp.scheduled_at || null,
          });
        });
      }

      // Processar campanhas SMS
      if (smsRes.status === 'fulfilled' && smsRes.value.data.success) {
        const smsCampaigns = smsRes.value.data.data || [];
        smsCampaigns.forEach((camp: any) => {
          allCampaigns.push({
            id: camp.id,
            name: camp.name || 'Campanha SMS sem nome',
            type: 'sms',
            status: camp.status || 'draft',
            totalRecipients: camp.recipients_count || 0,
            sentCount: camp.stats?.sent || 0,
            deliveredCount: camp.stats?.delivered || 0,
            createdAt: camp.created_at || new Date().toISOString(),
            scheduledAt: camp.scheduled_at || null,
          });
        });
      }

      // Processar campanhas de followups
      if (followupsRes.status === 'fulfilled' && followupsRes.value.data) {
        const followupCampaigns = followupsRes.value.data.data || followupsRes.value.data || [];
        followupCampaigns.forEach((camp: any) => {
          allCampaigns.push({
            id: camp.id,
            name: camp.name || 'Campanha de Followup sem nome',
            type: 'followup',
            status: camp.status || 'draft',
            totalRecipients: 0, // Followups não têm destinatários fixos
            sentCount: 0,
            deliveredCount: 0,
            createdAt: camp.created_at || new Date().toISOString(),
            scheduledAt: null,
          });
        });
      }

      setCampaigns(allCampaigns);
    } catch (err: any) {
      console.error('Erro ao buscar campanhas:', err);
      setError('Erro ao carregar campanhas. Tente novamente.');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = () => {
    setOpenDialog(true);
  };

  const handleSaveCampaign = () => {
    console.log('Saving campaign:', campaignForm);
    setOpenDialog(false);
    setCampaignForm({
      name: '',
      type: 'broadcast',
      message: '',
      channels: [],
      audience: 'all',
      scheduledDate: '',
      scheduledTime: '',
    });
  };

  const handleChannelChange = (channel: string) => {
    const newChannels = campaignForm.channels.includes(channel)
      ? campaignForm.channels.filter(c => c !== channel)
      : [...campaignForm.channels, channel];
    setCampaignForm({ ...campaignForm, channels: newChannels });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Campanhas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Crie e gerencie campanhas de marketing multicanal
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateCampaign}
        >
          Nova Campanha
        </Button>
      </Box>

      {/* Tipos de Campanhas */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Tipos de Campanhas
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Card 
            sx={{ 
              flex: '1 1 200px', 
              minWidth: 200, 
              cursor: 'pointer',
              '&:hover': { boxShadow: 4 }
            }}
            onClick={() => navigate('/campaigns/disparos')}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <SendIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Disparos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                WhatsApp, Telegram, Instagram
              </Typography>
            </CardContent>
          </Card>
          
          <Card 
            sx={{ 
              flex: '1 1 200px', 
              minWidth: 200, 
              cursor: 'pointer',
              '&:hover': { boxShadow: 4 }
            }}
            onClick={() => navigate('/campaigns/sms')}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <SmsIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                SMS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Campanhas de SMS em massa
              </Typography>
            </CardContent>
          </Card>
          
          <Card 
            sx={{ 
              flex: '1 1 200px', 
              minWidth: 200, 
              cursor: 'pointer',
              '&:hover': { boxShadow: 4 }
            }}
            onClick={() => navigate('/campaigns/voice')}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <CampaignIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Voz
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Campanhas de voz automatizadas
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : (
        <>
          {/* Métricas Gerais */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <CampaignIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {campaigns.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Campanhas
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <SendIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {campaigns.reduce((acc, camp) => acc + (camp.sentCount || 0), 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mensagens Enviadas
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <GroupIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {campaigns.reduce((acc, camp) => acc + (camp.totalRecipients || 0), 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Destinatários
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ScheduleIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {campaigns.filter(c => c.status === 'scheduled').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Campanhas Agendadas
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Lista de Campanhas */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Todas as Campanhas
              </Typography>
              {campaigns.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Nenhuma campanha encontrada
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Nome</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Destinatários</TableCell>
                        <TableCell align="center">Entregues</TableCell>
                        <TableCell align="center">Taxa de Abertura</TableCell>
                        <TableCell align="center">Taxa de Clique</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {campaign.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Criada em {new Date(campaign.createdAt).toLocaleDateString('pt-BR')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          campaign.type === 'disparo' ? 'Disparo' :
                          campaign.type === 'sms' ? 'SMS' :
                          campaign.type === 'followup' ? 'Followup' :
                          campaign.type.toUpperCase()
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(campaign.status)}
                        color={getStatusColor(campaign.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {(campaign.totalRecipients || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box>
                        <Typography variant="body2">
                          {(campaign.deliveredCount || 0).toLocaleString()}
                        </Typography>
                        {campaign.totalRecipients && campaign.totalRecipients > 0 && (
                          <LinearProgress
                            variant="determinate"
                            value={((campaign.deliveredCount || 0) / campaign.totalRecipients) * 100}
                            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {campaign.openRate && campaign.openRate > 0 ? `${campaign.openRate.toFixed(1)}%` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {campaign.clickRate && campaign.clickRate > 0 ? `${campaign.clickRate.toFixed(1)}%` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {campaign.type === 'disparo' && (
                          <IconButton 
                            size="small" 
                            onClick={() => navigate(`/campaigns/disparos/${campaign.id}`)}
                            title="Ver detalhes"
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        {campaign.type === 'sms' && (
                          <IconButton 
                            size="small" 
                            onClick={() => navigate(`/campaigns/sms/${campaign.id}`)}
                            title="Ver detalhes"
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Dialog para Nova Campanha */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nova Campanha</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nome da Campanha"
              value={campaignForm.name}
              onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Tipo de Campanha</InputLabel>
              <Select
                value={campaignForm.type}
                onChange={(e) => setCampaignForm({ ...campaignForm, type: e.target.value })}
                label="Tipo de Campanha"
              >
                <MenuItem value="broadcast">Disparo em Massa</MenuItem>
                <MenuItem value="sequence">Sequência</MenuItem>
                <MenuItem value="trigger">Gatilho</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Mensagem"
              multiline
              rows={4}
              value={campaignForm.message}
              onChange={(e) => setCampaignForm({ ...campaignForm, message: e.target.value })}
              fullWidth
              placeholder="Digite a mensagem que será enviada..."
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Canais de Envio
              </Typography>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campaignForm.channels.includes('whatsapp')}
                      onChange={() => handleChannelChange('whatsapp')}
                    />
                  }
                  label="WhatsApp"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campaignForm.channels.includes('sms')}
                      onChange={() => handleChannelChange('sms')}
                    />
                  }
                  label="SMS"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campaignForm.channels.includes('email')}
                      onChange={() => handleChannelChange('email')}
                    />
                  }
                  label="Email"
                />
              </FormGroup>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Público-Alvo</InputLabel>
              <Select
                value={campaignForm.audience}
                onChange={(e) => setCampaignForm({ ...campaignForm, audience: e.target.value })}
                label="Público-Alvo"
              >
                <MenuItem value="all">Todos os Contatos</MenuItem>
                <MenuItem value="new-leads">Novos Leads</MenuItem>
                <MenuItem value="qualified">Leads Qualificados</MenuItem>
                <MenuItem value="customers">Clientes</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Data de Agendamento"
                type="date"
                value={campaignForm.scheduledDate}
                onChange={(e) => setCampaignForm({ ...campaignForm, scheduledDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Horário"
                type="time"
                value={campaignForm.scheduledTime}
                onChange={(e) => setCampaignForm({ ...campaignForm, scheduledTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleSaveCampaign} variant="contained">
            Criar Campanha
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Campaigns; 